import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

/**
 * EKAA Admin Apollo Client
 * Configured to read JWT from localStorage for authentication
 */

// Config
const GRAPHQL_URI =
  import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/graphql`
    : 'https://api.e-kaa.app/graphql';

// Storage key (must match authProvider)
const TOKEN_KEY = 'ekaa_admin_token';

// HTTP Link
const httpLink = createHttpLink({
  uri: GRAPHQL_URI,
  credentials: 'include',
});

// Auth Link - Read token from localStorage for every request
const authLink = setContext((_, { headers }) => {
  // Always read fresh token from localStorage
  const token = localStorage.getItem(TOKEN_KEY);

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Error Link - Handle GraphQL and network errors
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      console.error(
        `[GraphQL Error] Message: ${err.message}, Location: ${JSON.stringify(err.locations)}, Path: ${err.path}`
      );

      // Handle authentication errors - redirect to login
      if (
        err.extensions?.code === 'UNAUTHENTICATED' ||
        err.message.includes('Unauthorized')
      ) {
        console.warn('Authentication error - clearing session');
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('ekaa_admin_refresh_token');
        localStorage.removeItem('ekaa_admin_user');
        // React Admin will handle the redirect via authProvider.checkError
      }

      // Handle forbidden errors
      if (
        err.extensions?.code === 'FORBIDDEN' ||
        err.message.includes('Forbidden')
      ) {
        console.error(`Access denied for operation: ${operation.operationName}`);
      }
    }
  }

  if (networkError) {
    console.error(`[Network Error]: ${networkError.message}`);

    // Handle network unavailable
    if ('statusCode' in networkError && networkError.statusCode === 503) {
      console.error('API Service Unavailable');
    }
  }
});

// Cache configuration
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // Pagination for profiles
        publicProfiles: {
          keyArgs: ['search', 'sector', 'city', 'country'],
          merge(existing, incoming, { args }) {
            if (!args?.offset || args.offset === 0) {
              return incoming;
            }
            return [...(existing || []), ...incoming];
          },
        },
        // Pagination for cards
        allDigitalCards: {
          keyArgs: false,
          merge(existing, incoming, { args }) {
            if (!args?.offset || args.offset === 0) {
              return incoming;
            }
            return [...(existing || []), ...incoming];
          },
        },
      },
    },
    Profile: {
      keyFields: ['id'],
    },
    DigitalCardObject: {
      keyFields: ['id'],
    },
  },
});

// Create Apollo Client instance
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

export default apolloClient;
